import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Link, Snackbar, Typography } from '@mui/material';
import { useNotify } from 'react-admin';
import { fetchTransportWaiting, giveBack, restoreIntake } from './api';
import { KewixLinkDialog } from './KewixLinkDialog';
import { KewixTakeOverDialog } from './KewixTakeOverDialog';
import { giveBackMessage, isUnlinked, regionCode, rowDetail, takenOverLine } from './kewixFormat';
import { TransportItem } from './types';

const POLL_MS = 30_000;

/** Kewix's transport queue as seen by staff; refetches every 30 s. */
function useKewixTransport(funeralId?: number) {
  const [items, setItems] = useState<TransportItem[]>([]);
  const refresh = useCallback(async () => {
    try {
      setItems(await fetchTransportWaiting(funeralId));
    } catch {
      // A failed poll keeps what is on screen; the next one corrects it.
    }
  }, [funeralId]);
  useEffect(() => {
    void refresh();
    const iv = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(iv);
  }, [refresh]);
  return { items, refresh };
}

const AMBER = {
  bg: 'rgba(245,158,11,0.08)',
  border: 'rgba(217,119,6,0.35)',
  dot: '#d97706',
};

const Row = ({
  item,
  showCase,
  onTakeOver,
  onGiveBack,
  onLink,
}: {
  item: TransportItem;
  showCase: boolean;
  onTakeOver: (i: TransportItem) => void;
  onGiveBack: (i: TransportItem) => void;
  onLink: (i: TransportItem) => void;
}) => {
  const waiting = item.state === 'waiting';
  const region = regionCode(item.region);
  const unlinked = isUnlinked(item);
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1.5}
      flexWrap="wrap"
      py={0.75}
      sx={{ color: waiting ? 'text.primary' : 'text.secondary' }}
    >
      {region && (
        <Box
          component="span"
          sx={{ fontSize: 11, fontWeight: 700, px: 0.75, borderRadius: 1, bgcolor: 'action.hover', flex: 'none' }}
        >
          {region}
        </Box>
      )}
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" component="span" fontWeight={waiting ? 600 : 400}>
          {item.deceasedName || 'Unnamed deceased'}
        </Typography>
        <Typography variant="body2" component="span" color="text.secondary">
          {' · '}
          {rowDetail(item)}
          {!waiting && ` · ${takenOverLine(item)}`}
        </Typography>
        {showCase && (
          <Typography variant="body2" component="span">
            {' · '}
            {item.funeralId ? (
              <Link component={RouterLink} to={`/funerals/${item.funeralId}/show`} underline="hover">
                Case {item.funeralId}
              </Link>
            ) : item.leadId ? (
              <Link component={RouterLink} to={`/leads/${item.leadId}/show`} underline="hover">
                Lead
              </Link>
            ) : (
              <Box component="span" sx={{ color: waiting ? AMBER.dot : 'text.secondary', fontWeight: 600 }}>
                Not linked
              </Box>
            )}
          </Typography>
        )}
      </Box>
      {showCase && unlinked && (
        <Button size="small" sx={{ textTransform: 'none' }} onClick={() => onLink(item)}>
          Link to case
        </Button>
      )}
      {waiting ? (
        <Button size="small" variant="contained" disableElevation sx={{ textTransform: 'none' }} onClick={() => onTakeOver(item)}>
          Take over
        </Button>
      ) : (
        <Button size="small" color="inherit" sx={{ textTransform: 'none' }} onClick={() => onGiveBack(item)}>
          Give back
        </Button>
      )}
    </Box>
  );
};

/**
 * "Kewix is waiting for transport": every death Kewix logged with transport
 * requested that is still in their queue (or that we took over and haven't
 * finished with). Renders nothing when there is nothing to show.
 *
 * With `funeralId` it is the case-page banner: only that case's calls, and no
 * case link (you are on it).
 */
export const KewixTransportStrip = ({ funeralId }: { funeralId?: number }) => {
  const { items, refresh } = useKewixTransport(funeralId);
  const notify = useNotify();
  const [takeOverItem, setTakeOverItem] = useState<TransportItem | null>(null);
  const [linkItem, setLinkItem] = useState<TransportItem | null>(null);
  const [dismissed, setDismissed] = useState<TransportItem | null>(null);

  const handleGiveBack = async (item: TransportItem) => {
    try {
      const res = await giveBack(item.id);
      const msg = giveBackMessage(res.result);
      notify(msg.text, { type: msg.ok ? 'success' : 'warning' });
    } catch {
      notify("Couldn't give this back to Kewix", { type: 'error' });
    }
    void refresh();
  };

  const handleUndoDismiss = async () => {
    const item = dismissed;
    setDismissed(null);
    if (!item) return;
    try {
      await restoreIntake(item.id);
      notify('Restored', { type: 'success' });
    } catch {
      notify("Couldn't restore this call", { type: 'error' });
    }
    void refresh();
  };

  const anyWaiting = items.some((i) => i.state === 'waiting');

  return (
    <>
      {items.length > 0 && (
        <Box
          sx={{
            mt: funeralId ? 0 : 2,
            mb: funeralId ? 1 : 0,
            mx: funeralId ? 2 : 0,
            px: 2,
            py: 1,
            borderRadius: 2,
            border: 1,
            borderColor: anyWaiting ? AMBER.border : 'divider',
            bgcolor: anyWaiting ? AMBER.bg : 'transparent',
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {anyWaiting && (
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: AMBER.dot, flex: 'none' }} />
            )}
            <Typography variant="body2" fontWeight={700}>
              {anyWaiting ? 'Kewix is waiting for transport' : 'Transport taken over from Kewix'}
            </Typography>
          </Box>
          {items.map((item) => (
            <Row
              key={item.id}
              item={item}
              showCase={!funeralId}
              onTakeOver={setTakeOverItem}
              onGiveBack={(i) => void handleGiveBack(i)}
              onLink={setLinkItem}
            />
          ))}
        </Box>
      )}
      <KewixTakeOverDialog item={takeOverItem} onClose={() => setTakeOverItem(null)} onDone={() => void refresh()} />
      <KewixLinkDialog
        item={linkItem}
        onClose={() => setLinkItem(null)}
        onChanged={() => void refresh()}
        onDismissed={setDismissed}
      />
      <Snackbar
        open={!!dismissed}
        autoHideDuration={10_000}
        onClose={(_, why) => why !== 'clickaway' && setDismissed(null)}
        message="Kewix call dismissed — Kewix won't dispatch it"
        action={
          <Button color="inherit" size="small" onClick={() => void handleUndoDismiss()}>
            Undo
          </Button>
        }
      />
    </>
  );
};
